/* 606 Strength & Conditioning — theme behaviour
   No dependencies. Everything here is progressive: the site works without it. */
(function () {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- drawers */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  class Overlay {
    constructor(root, { panel, overlay, openClass = 'translate-x-0' }) {
      this.root = root;
      this.panel = $(panel, root);
      this.overlay = $(overlay, root);
      this.openClass = openClass;
      this.isOpen = false;
      this.lastFocus = null;
      this.onKey = this.onKey.bind(this);
    }

    open() {
      if (!this.root || this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.root.classList.remove('pointer-events-none');
      this.root.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        this.overlay && this.overlay.classList.replace('opacity-0', 'opacity-100');
        if (this.panel) {
          this.panel.classList.remove('translate-x-full', 'opacity-0', '-translate-y-3');
          this.panel.classList.add('translate-x-0', 'opacity-100');
        }
        const first = this.panel && $(FOCUSABLE, this.panel);
        first && first.focus({ preventScroll: true });
      });

      document.addEventListener('keydown', this.onKey);
    }

    close() {
      if (!this.root || !this.isOpen) return;
      this.isOpen = false;
      this.root.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.overlay && this.overlay.classList.replace('opacity-100', 'opacity-0');

      if (this.panel) {
        this.panel.classList.remove('translate-x-0', 'opacity-100');
        this.panel.classList.add(this.root.hasAttribute('data-search-modal') ? 'opacity-0' : 'translate-x-full');
        if (this.root.hasAttribute('data-search-modal')) this.panel.classList.add('-translate-y-3');
      }

      window.setTimeout(() => {
        if (!this.isOpen) this.root.classList.add('pointer-events-none');
      }, reduceMotion ? 0 : 420);

      document.removeEventListener('keydown', this.onKey);
      this.lastFocus && this.lastFocus.focus({ preventScroll: true });
    }

    onKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.panel) return;

      const items = $$(FOCUSABLE, this.panel).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const menu = $('[data-menu-drawer]')
    ? new Overlay($('[data-menu-drawer]'), { panel: '[data-menu-panel]', overlay: '[data-menu-overlay]' })
    : null;

  const search = $('[data-search-modal]')
    ? new Overlay($('[data-search-modal]'), { panel: '[data-search-panel]', overlay: '[data-search-overlay]' })
    : null;

  let cart = $('[data-cart-drawer]')
    ? new Overlay($('[data-cart-drawer]'), { panel: '[data-cart-panel]', overlay: '[data-cart-overlay]' })
    : null;

  document.addEventListener('click', (event) => {
    const t = event.target;

    if (t.closest('[data-menu-open]')) return menu && menu.open();
    if (t.closest('[data-menu-close]') || t.closest('[data-menu-overlay]')) return menu && menu.close();

    if (t.closest('[data-search-open]')) {
      if (!search) return;
      search.open();
      window.setTimeout(() => {
        const input = $('[data-search-input]');
        input && input.focus();
      }, 120);
      return;
    }
    if (t.closest('[data-search-close]') || t.closest('[data-search-overlay]')) return search && search.close();

    if (t.closest('[data-cart-open]')) return cart && cart.open();
    if (t.closest('[data-cart-close]') || t.closest('[data-cart-overlay]')) return cart && cart.close();
  });

  /* ------------------------------------------------------------------- cart */
  const routes = window.routes || {};
  const strings = window.cartStrings || {};

  async function refreshCart() {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;

    const response = await fetch(`${window.location.pathname}?sections=cart-drawer`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    const data = await response.json();
    const markup = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');

    const fresh = $('[data-cart-contents]', markup);
    const current = $('[data-cart-contents]', drawer);
    if (fresh && current) current.innerHTML = fresh.innerHTML;

    const state = await (await fetch(`${routes.cart_url || '/cart'}.js`)).json();
    const badge = $('[data-cart-count]');
    if (badge) {
      badge.textContent = state.item_count;
      badge.classList.toggle('hidden', state.item_count === 0);
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: state }));
  }

  /* Add to cart — works for merch quick-add and membership selling plans alike. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-cart-form]');
    if (!form) return;
    event.preventDefault();

    const button = $('[data-cart-submit]', form);
    const label = button && $('[data-submit-label]', button);
    const original = label ? label.textContent : '';

    if (button) button.disabled = true;
    if (label) label.textContent = '…';

    try {
      const body = new FormData(form);
      body.append('sections', 'cart-drawer');

      const response = await fetch(routes.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || strings.error);
      }

      await refreshCart();
      cart && cart.open();
    } catch (error) {
      if (label) label.textContent = error.message || strings.error || 'Try again';
      window.setTimeout(() => {
        if (label) label.textContent = original;
      }, 2600);
      return;
    } finally {
      if (button) button.disabled = false;
      if (label && label.textContent === '…') label.textContent = original;
    }
  });

  /* Quantity and remove inside the drawer. */
  document.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-qty-change]');
    if (!control) return;
    event.preventDefault();

    control.disabled = true;
    const row = control.closest('[data-line-item]');
    row && row.classList.add('opacity-40', 'pointer-events-none');

    await fetch(routes.cart_change_url || '/cart/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: control.dataset.key, quantity: Number(control.dataset.qty) }),
    });

    await refreshCart();
  });


  /* --------------------------------------------------------- variant picker */
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-option-selector]')) return;

    const form = event.target.closest('form');
    const select = form && $('[data-variant-select]', form);
    if (!select) return;

    const chosen = $$('[data-option-selector]:checked', form).map((input) => input.value).join(' / ');
    const match = $$('option', select).find((option) => option.dataset.options === chosen);
    if (!match) return;

    select.value = match.value;
    const button = $('[data-cart-submit]', form);
    const label = button && $('[data-submit-label]', button);
    if (button) button.disabled = match.disabled;
    if (label) label.textContent = match.disabled ? 'Sold out' : 'Add to cart';

    const url = new URL(window.location);
    url.searchParams.set('variant', match.value);
    window.history.replaceState({}, '', url);
  });

  /* --------------------------------------------------------- header on scroll */
  const header = $('[data-header]');
  const bar = $('[data-header-bar]');

  if (header && bar) {
    const glass = ['bg-white/80', 'backdrop-blur-glass', 'backdrop-saturate-150', 'border-ink-600'];
    let ticking = false;

    const update = () => {
      const stuck = window.scrollY > 24;
      glass.forEach((cls) => bar.classList.toggle(cls, stuck));
      bar.classList.toggle('border-transparent', !stuck);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  }

  /* ---------------------------------------------------------- timetable days */
  function initTimetable(root) {
    $$('[data-day-panel]', root).length && $$('[role="tablist"][aria-label="Choose a day"]', root).forEach(function (list) {
      var section = list.closest('section') || document;
      var panels = $$('[data-day-panel]', section);
      var tabs = $$('[data-day-tab]', list);

      function select(slug) {
        panels.forEach(function (p) { p.classList.toggle('is-active', p.dataset.dayPanel === slug); });
        tabs.forEach(function (t) { t.setAttribute('aria-selected', String(t.dataset.dayTab === slug)); });
      }

      list.addEventListener('click', function (event) {
        var tab = event.target.closest('[data-day-tab]');
        if (tab) select(tab.dataset.dayTab);
      });

      // Open on today where there is a session, otherwise the first day listed.
      var todayPanel = panels.filter(function (p) { return p.dataset.today === 'true'; })[0];
      select((todayPanel || panels[0]).dataset.dayPanel);
    });
  }

  initTimetable(document);

  /* ------------------------------------------------------------ scroll reveal */
  function observeReveals(root = document) {
    const targets = $$('[data-reveal]', root).filter((el) => !el.classList.contains('is-in'));
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => io.observe(el));
  }

  observeReveals();

  /* ------------------------------------------------------ theme editor events */
  document.addEventListener('shopify:section:load', (event) => {
    observeReveals(event.target);
    initTimetable(event.target);
    if (event.detail.sectionId === 'cart-drawer' || $('[data-cart-drawer]', event.target)) {
      const root = $('[data-cart-drawer]');
      if (root) cart = new Overlay(root, { panel: '[data-cart-panel]', overlay: '[data-cart-overlay]' });
    }
  });
})();
